// pages/admin/AdminSettings.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

const AdminSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentTab = location.pathname.split('/')[3] || 'general';

  const handleTabChange = (tab) => {
    navigate(`/admin/settings/${tab}`);
  };

  return (
    <div className="admin-settings">
      <div className="admin-page-header">
        <h2>System Settings</h2>
        <p>Configure application settings and parameters</p>
      </div>

      <div className="admin-settings-container">
        <div className="admin-settings-sidebar">
          <div className="admin-settings-nav">
            <SettingsNavItem 
              id="general"
              icon="fas fa-cog"
              label="General Settings"
              active={currentTab === 'general'}
              onClick={() => handleTabChange('general')}
            />
            <SettingsNavItem 
              id="subscription"
              icon="fas fa-credit-card"
              label="Subscription Rules"
              active={currentTab === 'subscription'}
              onClick={() => handleTabChange('subscription')}
            />
            <SettingsNavItem 
              id="endowment"
              icon="fas fa-hand-holding-heart"
              label="Endowment Delivery"
              active={currentTab === 'endowment'}
              onClick={() => handleTabChange('endowment')}
            />
            <SettingsNavItem 
              id="notification"
              icon="fas fa-bell"
              label="Notifications"
              active={currentTab === 'notification'}
              onClick={() => handleTabChange('notification')}
            />
            <SettingsNavItem 
              id="content"
              icon="fas fa-file-alt"
              label="Static Content"
              active={currentTab === 'content'}
              onClick={() => handleTabChange('content')}
            />
            <SettingsNavItem 
              id="security"
              icon="fas fa-shield-alt"
              label="Security Settings"
              active={currentTab === 'security'}
              onClick={() => handleTabChange('security')}
            />
          </div>
        </div>

        <div className="admin-settings-content">
          <Routes>
            <Route path="/" element={<Navigate to="/admin/settings/general" replace />} />
            <Route path="/general" element={<GeneralSettings />} />
            <Route path="/subscription" element={<SubscriptionSettings />} />
            <Route path="/endowment" element={<PlaceholderSettings title="Endowment Delivery" />} />
            <Route path="/notification" element={<PlaceholderSettings title="Notifications" />} />
            <Route path="/content" element={<PlaceholderSettings title="Static Content" />} />
            <Route path="/security" element={<PlaceholderSettings title="Security Settings" />} />
            <Route path="*" element={<Navigate to="/admin/settings/general" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const SettingsNavItem = ({ id, icon, label, active, onClick }) => (
  <a 
    href="#" 
    className={`admin-settings-nav-item ${active ? 'active' : ''}`}
    onClick={(e) => {
      e.preventDefault();
      onClick();
    }}
  >
    <i className={icon}></i>
    <span>{label}</span>
  </a>
);

const GeneralSettings = () => {
  const { apiCall, addNotification } = useAdmin();
  const [settings, setSettings] = useState({
    appName: 'PhenixLoc',
    appUrl: 'https://phenixloc.com',
    appEmail: 'support@phenixloc.com',
    companyName: 'BitMobile Technologies',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY'
  });
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate form
      if (!settings.appName.trim()) {
        throw new Error('Application name is required');
      }

      if (!isValidUrl(settings.appUrl.trim())) {
        throw new Error('Please enter a valid URL');
      }

      if (!isValidEmail(settings.appEmail.trim())) {
        throw new Error('Please enter a valid email address');
      }

      // In a real implementation, this would save to the API
      // For now, just simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));

      addNotification('General settings saved successfully', 'success');
      setIsDirty(false);

    } catch (error) {
      addNotification(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      appName: 'PhenixLoc',
      appUrl: 'https://phenixloc.com',
      appEmail: 'support@phenixloc.com',
      companyName: 'BitMobile Technologies',
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY'
    });
    setIsDirty(false);
    addNotification('General settings reset to default values', 'info');
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div className="admin-settings-panel">
      <h3>General Settings</h3>
      
      <div className="admin-settings-section">
        <h4>Application Information</h4>
        <div className="admin-form-group">
          <label htmlFor="app-name">Application Name</label>
          <input
            type="text"
            id="app-name"
            value={settings.appName}
            onChange={(e) => handleInputChange('appName', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="app-url">Website URL</label>
          <input
            type="text"
            id="app-url"
            value={settings.appUrl}
            onChange={(e) => handleInputChange('appUrl', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="app-email">Contact Email</label>
          <input
            type="email"
            id="app-email"
            value={settings.appEmail}
            onChange={(e) => handleInputChange('appEmail', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="company-name">Company Name</label>
          <input
            type="text"
            id="company-name"
            value={settings.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
          />
        </div>
      </div>
      
      <div className="admin-settings-section">
        <h4>Regional Settings</h4>
        <div className="admin-form-group">
          <label htmlFor="timezone">Default Timezone</label>
          <select 
            id="timezone"
            value={settings.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time (US & Canada)</option>
            <option value="America/Chicago">Central Time (US & Canada)</option>
            <option value="America/Denver">Mountain Time (US & Canada)</option>
            <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
          </select>
        </div>
        <div className="admin-form-group">
          <label htmlFor="date-format">Date Format</label>
          <select 
            id="date-format"
            value={settings.dateFormat}
            onChange={(e) => handleInputChange('dateFormat', e.target.value)}
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
      </div>

      <div className="admin-settings-actions">
        <button 
          className="admin-btn admin-btn-primary" 
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
        <button 
          className="admin-btn admin-btn-secondary" 
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Default
        </button>
      </div>
    </div>
  );
};

const SubscriptionSettings = () => {
  const { apiCall, addNotification } = useAdmin();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSubscriptionPlans();
  }, []);

  const loadSubscriptionPlans = async () => {
    try {
      setLoading(true);
      
      // For demo purposes, using mock data
      // In real implementation, this would call the API
      const mockPlans = [
        {
          id: 1,
          name: 'Basic Plan',
          price: 9.99,
          duration: 'monthly',
          description: 'Essential features for personal use',
          features: ['Basic file storage', 'Email support', '1 inheritance'],
          storage: 100
        },
        {
          id: 2,
          name: 'Plus Plan',
          price: 19.99,
          duration: 'monthly',
          description: 'Advanced features for power users',
          features: ['Extended storage', 'Priority support', 'Unlimited inheritances', 'Advanced security'],
          storage: 1000
        }
      ];

      setPlans(mockPlans);
    } catch (error) {
      addNotification('Error loading subscription plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = (planId, field, value) => {
    setPlans(prev => prev.map(plan => 
      plan.id === planId 
        ? { ...plan, [field]: value }
        : plan
    ));
  };

  const handleFeatureChange = (planId, featureIndex, value) => {
    setPlans(prev => prev.map(plan => 
      plan.id === planId 
        ? {
            ...plan,
            features: plan.features.map((feature, index) => 
              index === featureIndex ? value : feature
            )
          }
        : plan
    ));
  };

  const addFeature = (planId) => {
    setPlans(prev => prev.map(plan => 
      plan.id === planId 
        ? { ...plan, features: [...plan.features, ''] }
        : plan
    ));
  };

  const removeFeature = (planId, featureIndex) => {
    setPlans(prev => prev.map(plan => 
      plan.id === planId 
        ? {
            ...plan,
            features: plan.features.filter((_, index) => index !== featureIndex)
          }
        : plan
    ));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate plans
      for (const plan of plans) {
        if (!plan.name.trim()) {
          throw new Error('Plan name is required');
        }
        if (isNaN(parseFloat(plan.price)) || parseFloat(plan.price) < 0) {
          throw new Error('Plan price must be a valid number');
        }
        if (isNaN(parseInt(plan.storage)) || parseInt(plan.storage) <= 0) {
          throw new Error('Storage limit must be a positive number');
        }
      }

      // In real implementation, this would save to the API
      await new Promise(resolve => setTimeout(resolve, 1000));

      addNotification('Subscription plans saved successfully', 'success');
    } catch (error) {
      addNotification(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-settings-panel">
        <h3>Subscription Rules</h3>
        <div className="admin-loading-data">
          <p>Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings-panel">
      <h3>Subscription Rules</h3>
      
      <div className="admin-subscription-plans-container">
        {plans.map((plan, planIndex) => (
          <div key={plan.id} className="admin-settings-section admin-subscription-plan">
            <h4>
              <input
                type="text"
                className="admin-plan-name"
                value={plan.name}
                onChange={(e) => handlePlanChange(plan.id, 'name', e.target.value)}
                placeholder="Plan Name"
              />
            </h4>
            
            <div className="admin-form-group">
              <label htmlFor={`plan-price-${planIndex}`}>Monthly Price ($)</label>
              <input
                type="number"
                id={`plan-price-${planIndex}`}
                value={plan.price}
                onChange={(e) => handlePlanChange(plan.id, 'price', e.target.value)}
                step="0.01"
                min="0"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor={`plan-duration-${planIndex}`}>Billing Cycle</label>
              <select
                id={`plan-duration-${planIndex}`}
                value={plan.duration}
                onChange={(e) => handlePlanChange(plan.id, 'duration', e.target.value)}
              >
                <option value="monthly">Monthly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor={`plan-storage-${planIndex}`}>Storage Limit (MB)</label>
              <input
                type="number"
                id={`plan-storage-${planIndex}`}
                value={plan.storage}
                onChange={(e) => handlePlanChange(plan.id, 'storage', e.target.value)}
                min="1"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor={`plan-description-${planIndex}`}>Description</label>
              <textarea
                id={`plan-description-${planIndex}`}
                value={plan.description}
                onChange={(e) => handlePlanChange(plan.id, 'description', e.target.value)}
                rows="3"
              />
            </div>
            
            <div className="admin-form-group">
              <label>Features</label>
              <div className="admin-plan-features">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="admin-feature-input">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(plan.id, featureIndex, e.target.value)}
                      placeholder={`Feature ${featureIndex + 1}`}
                    />
                    <button
                      type="button"
                      className="admin-btn-icon admin-btn-danger"
                      onClick={() => removeFeature(plan.id, featureIndex)}
                      title="Remove feature"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => addFeature(plan.id)}
                >
                  <i className="fas fa-plus"></i>
                  Add Feature
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-settings-actions">
        <button 
          className="admin-btn admin-btn-primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
        <button 
          className="admin-btn admin-btn-secondary" 
          onClick={loadSubscriptionPlans}
          disabled={saving}
        >
          Reset to Default
        </button>
      </div>
    </div>
  );
};

const PlaceholderSettings = ({ title }) => {
  const { addNotification } = useAdmin();

  return (
    <div className="admin-settings-panel">
      <h3>{title}</h3>
      <div className="admin-placeholder-content">
        <div className="admin-placeholder-icon">
          <i className="fas fa-cog"></i>
        </div>
        <h4>Coming Soon</h4>
        <p>This settings section is not yet implemented.</p>
        <button 
          className="admin-btn admin-btn-primary"
          onClick={() => addNotification(`${title} settings will be available in a future update`, 'info')}
        >
          <i className="fas fa-bell"></i>
          Notify When Available
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;